// V4-FRONT-ANY-USAGE batch 4 2026-05-28
import { useState, memo } from 'react';
import { useDrag } from 'react-dnd';
import { Image, Edit, AlertTriangle, BookOpen, Loader2, LayoutTemplate, BarChart3 } from 'lucide-react';
import { PostAnalyticsModal } from './PostAnalyticsModal';
import type { Post, CommunicationChannel, ChannelType } from '../../../types';
import { useImageGenerationContext } from '../../../contexts/ImageGenerationContext';
import { useCarouselGenerationContext } from '../../../contexts/CarouselGenerationContext';
import { defaultChannels, getCharacterLimit } from '../../../config/channels';
import { useLanguage } from '../../../contexts/LanguageContext';
import { formatDateTime, formatDateLong } from '../../../utils/dateUtils';
import { channelIcons, ChannelSvgIcon, channelSvgIcons, PostStatus } from '../../../utils/Constants';
import { StatusChip } from './StatusChip';

interface PostCardProps {
  post: Post;
  onClick?: () => void;
  showScheduledDate?: boolean;
  onRemove?: () => void;
  onDelete?: () => void;
  onSelect?: () => void;
  cardWidth?: number;
  communicationChannels?: CommunicationChannel[];
  isInQueue?: boolean; // Force l'opacité à 100% si dans la queue
  onScheduleClick?: () => void; // Callback pour ouvrir l'agenda
}

function PostCardInner({ post, onClick, showScheduledDate, onRemove, onDelete, onSelect, cardWidth, communicationChannels, isInQueue, onScheduleClick }: PostCardProps) {
  const { t } = useLanguage();
  const { isImagePending } = useImageGenerationContext();
  const { isCarouselPending } = useCarouselGenerationContext();
  const isImageGenerating = post.id ? isImagePending(post.id) : false;
  const isCarouselGenerating = post.id ? isCarouselPending(post.id) : false;
  // V4-ZERNIO-ANALYTICS-POST-CARD 2026-05-28 — modale drill-down sur les posts publiés
  const [showAnalytics, setShowAnalytics] = useState(false);
  const hasCarousel = post.mediaType === 'carousel' || !!post.carouselId;
  
  const commChannel = communicationChannels?.find(c => c.id === post.channelId);
  const canDragPost = !!commChannel?.zernioAccountId && commChannel?.zernioStatus === 'connected';

  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'post',
    item: () => ({
      ...post,
      // Create a deep copy of the date to avoid mutation issues
      scheduledDate: post.scheduledDate ? new Date(post.scheduledDate) : undefined
    }),
    canDrag: () => canDragPost,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [post, canDragPost]); // Re-create drag when post or drag permission changes

  const handleClick = () => {
    // Note (2026-05) : auparavant on bloquait le clic sur les posts programmés en
    // galerie (`post.scheduledDate && !isInQueue`). C'était une fausse bonne idée :
    // l'utilisateur ne pouvait plus consulter le contenu d'un post planifié.
    // Aujourd'hui on autorise le clic — l'opacité 0.25 signale l'état "déjà programmé"
    // sans empêcher la consultation.
    if (onClick) onClick();
    if (onSelect) onSelect();
  };

  const getChannelName = () => {
    if (post.isNewsletter) {
      return t.menu.newsletter;
    }
    const channel = communicationChannels?.find(c => c.id === post.channelId);
    if (channel) {
      return channel.customName || channel.network;
    }
    if (post.network) {
      return post.network;
    }
    if (!post.channelId) {
      return t.common.draft || 'Brouillon';
    }
    return t.common.unknownChannel;
  };

  const getChannelIcon = (): string => {
    // Special case for newsletters
    if (post.isNewsletter) {
      return '✉';
    }
    
    // Retourne un emoji/symbole pour chaque réseau social
    // Include 'Twitter' alias for backwards compatibility with old data
    const icons: Record<ChannelType | string, string> = {
      'X': 'X',
      'Twitter': 'X', // Alias for backwards compatibility
      'Facebook': 'f',
      'Instagram': 'IG',
      'LinkedIn': 'in',
      'TikTok': '♪',
      'YouTube': '▶', 'Newsletter': '✉', 'Slack': 'S', 'WhatsApp': 'W', 'Telegram': 'T', 'Teams': 'M', 'Discord': 'D',
      'Pinterest': 'P',
      'Baidu': 'B',
    };
    if (commChannel?.network) {
      return icons[commChannel.network] || commChannel.network.charAt(0);
    }
    if (post.network) {
      return icons[post.network] || post.network.charAt(0);
    }
    return '?';
  };

  const networkName = commChannel?.network || post.network;
  const networkConfig = post.isNewsletter
    ? { color: '', textColor: '', style: { backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' } } // Purple for newsletters
    : (networkName ? defaultChannels.find(c => c.name === networkName) : undefined);

  // Character count and limit
  const charCount = post.content?.length || 0;
  const charLimit = commChannel ? getCharacterLimit(commChannel.network) : Infinity;
  const isOverLimit = charCount > charLimit;

  // Calcul de l'opacité : 100% si dans la queue, 25% si scheduledDate existe (dans la galerie), 100% sinon
  const cardOpacity = isDragging ? 0.5 : (isInQueue ? 1 : (post.scheduledDate ? 0.25 : 1));
  // V4-UX-SCHEDULED-OPACITY-TOOLTIP 2026-07-02 — l'opacité 25 % seule était muette :
  // l'utilisateur ne comprenait pas pourquoi un post "grisé" restait en galerie.
  const isDimmedScheduled = !isInQueue && !!post.scheduledDate && !isDragging;

  return (
      <div
        ref={(node) => { drag(node); }}
        onClick={handleClick}
        data-testid="post-card"
        data-post-id={post.id}
        data-post-status={post.status ?? 'unknown'}
        title={isDimmedScheduled ? t.posts.scheduledCardTooltip : undefined}
        className="rounded-lg p-3 shadow-sm hover:shadow-md transition-all flex-shrink-0 flex flex-col gap-2"
      style={{
        opacity: cardOpacity,
        cursor: canDragPost ? 'grab' : 'pointer',
        width: cardWidth ? `${cardWidth}px` : undefined,
        height: '220px',
        backgroundColor: 'var(--color-background)', // Cards use "fond" like header and menus
        border: `1px solid var(--color-border)`,
      }}
    >
      {/* Header avec canal et date */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {(() => {
            const networkForIcon = commChannel?.network || post.network;
            return networkForIcon && channelSvgIcons[networkForIcon.toLowerCase()] ? (
              <ChannelSvgIcon network={networkForIcon.toLowerCase()} size={32} />
            ) : (
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${networkConfig?.color} ${networkConfig?.textColor} font-semibold`}
              style={{ fontSize: '13px', ...(networkConfig && 'style' in networkConfig ? networkConfig.style : {}) }}
            >
              {getChannelIcon()}
            </div>
            );
          })()}
          <span className="text-xs font-medium uppercase tracking-wide whitespace-nowrap truncate" style={{ color: 'var(--color-text-secondary)' }}>
            {getChannelName()}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Chip de statut (rien pour draft/pending) — affiché en haut-droite */}
          <StatusChip status={post.status} compact />
          {showScheduledDate && post.scheduledDate && onScheduleClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onScheduleClick?.();
              }}
              className="w-6 h-6 rounded-full flex items-center justify-center transition-colors flex-shrink-0"
              style={{
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-background)'} // Cards use "fond"
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title={t.posts.editScheduleTitle}
            >
              <Edit className="h-3.5 w-3.5" style={{ color: 'var(--color-text-secondary)' }} />
            </button>
          )}
        </div>
      </div>
      
      {/* Contenu principal */}
      <div className="flex-1 overflow-hidden">
        <p className="leading-relaxed overflow-hidden" style={{ fontSize: '13px', fontWeight: 300, color: 'var(--color-text-primary)', display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {post.content}
        </p>
      </div>

      {/* Footer avec date et compteur de caractères */}
      <div className="flex items-center justify-between pt-1.5 gap-2" style={{ borderTop: `1px solid var(--color-border)` }}>
        <span className="text-xs font-medium truncate" style={{ color: 'var(--color-text-muted)' }}>
          {/*
            Date affichée — contextuelle au statut :
              - PUBLISHED + publishedDate    → "Publié le {date}"
              - status SCHEDULED ou scheduledDate présent → "Programmé le {date}"
              - sinon (draft / pending)      → "Créé le {date}" (comportement legacy)
            Note : le statut SCHEDULED prime sur showScheduledDate pour couvrir le cas
            galerie où la carte est rendue sans `showScheduledDate=true`.
          */}
          {post.status === PostStatus.PUBLISHED && post.publishedDate ? (
            <>
              {t.posts.publishedOn}: {formatDateLong(post.publishedDate)}
            </>
          ) : (post.status === PostStatus.SCHEDULED || (showScheduledDate && post.scheduledDate)) && post.scheduledDate ? (
            <>
              {t.posts.scheduledOn ?? t.posts.scheduledFor}: {formatDateTime(post.scheduledDate)}
            </>
          ) : (
            <>
              {t.posts.createdOn}: {formatDateLong(post.generatedDate)}
            </>
          )}
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* V4-ZERNIO-ANALYTICS-POST-CARD 2026-05-28 — bouton analytics pour les posts publiés */}
          {post.status === PostStatus.PUBLISHED && post.id && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowAnalytics(true);
              }}
              className="flex items-center justify-center rounded-full hover:scale-110 transition-transform"
              style={{ color: 'var(--color-primary)', width: 24, height: 24 }}
              title={t.postAnalytics.viewBtn}
              aria-label={t.postAnalytics.viewBtn}
              data-testid="post-card-analytics-button"
            >
              <BarChart3 className="h-3.5 w-3.5" />
            </button>
          )}
          {/* Character count indicator */}
          <span
            className={`text-xs font-semibold flex items-center gap-1 ${isOverLimit ? 'text-red-500' : ''}`}
            style={{ color: isOverLimit ? undefined : 'var(--color-text-secondary)' }}
            title={charLimit !== Infinity ? `${charCount}/${charLimit} ${t.common.characters}` : `${charCount} ${t.common.characters}`}
          >
            {isOverLimit && <AlertTriangle className="h-3 w-3" />}
            {charCount}{charLimit !== Infinity && `/${charLimit}`}
          </span>
          {/* V4-GEN-COMFORT-SOURCING 2026-06-01 — le badge "Sources limitées" a été RETIRÉ :
              le sourcing itère désormais (élargissement temporel) pour garantir assez de
              matière, et l'orchestrateur garantit ≥1 post. `knowledgeSourceQuality` reste
              porté sur le post pour la télémétrie, mais n'est plus affiché à l'utilisateur. */}
          {/* Source indicator */}
          {post.sourceReferences && post.sourceReferences.length > 0 && (
            <div 
              className="flex items-center gap-1" 
              style={{ color: 'var(--color-text-muted)' }} 
              title={`${post.sourceReferences.length} source(s): ${post.sourceReferences.map(s => s.title).join(', ')}`}
            >
              <BookOpen className="h-3.5 w-3.5" />
              <span className="text-xs">{post.sourceReferences.length}</span>
            </div>
          )}
          {isImageGenerating && (
            <div style={{ color: 'var(--color-primary)' }} title="Génération d'image en cours...">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            </div>
          )}
          {!isImageGenerating && post.imageUrl && !hasCarousel && (
            <div style={{ color: 'var(--color-text-muted)' }} title={t.newsletter.containsImage}>
              <Image className="h-3.5 w-3.5" />
            </div>
          )}
          {isCarouselGenerating && (
            <div style={{ color: 'var(--color-primary)' }} title={t.carousel.generating}>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            </div>
          )}
          {!isCarouselGenerating && hasCarousel && (
            <div style={{ color: 'var(--color-text-muted)' }} title={t.carousel.containsCarousel}>
              <LayoutTemplate className="h-3.5 w-3.5" />
            </div>
          )}
          {/* Compliance score badge */}
          {post.complianceScores && (post.complianceScores.positioning_score != null || post.complianceScores.tone_score != null || post.complianceScores.originality_score != null) && (() => {
            const p = post.complianceScores!.positioning_score ?? 0;
            const to = post.complianceScores!.tone_score ?? 0;
            const o = post.complianceScores!.originality_score ?? 0;
            const avg = (p + to + o) / 3;
            const pct = Math.round(avg * 100);
            // V4-DESIGN-SCORE-RAMP — tokens design system au lieu de hex direct
            const color = avg >= 0.7
              ? 'var(--color-success)'
              : avg >= 0.4
                ? 'var(--color-warning)'
                : 'var(--color-danger)';
            return (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none"
                style={{ backgroundColor: color, color: '#fff' }}
                title={`Score: ${pct}%`}
              >
                {pct}%
              </span>
            );
          })()}
        </div>
      </div>
      {/* V4-ZERNIO-ANALYTICS-POST-CARD 2026-05-28 — modale analytics rendue en portal-like */}
      {showAnalytics && post.id && (
        <PostAnalyticsModal
          postId={post.id}
          postTitle={post.content?.slice(0, 60)}
          onClose={() => setShowAnalytics(false)}
        />
      )}
    </div>
  );
}

// V4-FRONT-NO-OPTIMIZATIONS 2026-05-29 — memo pour éviter re-renders inutiles
// quand la prop list `filteredPosts` change mais que la carte elle-même n'a pas
// changé (shallow compare sur post + communicationChannels).
export const PostCard = memo(PostCardInner);
